import { useEffect, useMemo, useState } from "react";
import { apiFetch } from "../lib/api-client";
import { Messages } from "./Communications";
import type { CommunicationMessage } from "./communications-model";
type HistoricalThread = { id: string; phone: string | null; messageCount: number; lastMessageAt: string | null; summary: string | null };

function JobAutomatedMessages({ jobId, timezone }: { jobId: string; timezone?: string }) {
  const [messages, setMessages] = useState<CommunicationMessage[] | null>(null);
  const [error, setError] = useState("");
  useEffect(() => {
    const controller = new AbortController();
    void apiFetch(`/api/v1/workspace/jobs/${encodeURIComponent(jobId)}/automated-messages`, { signal: controller.signal })
      .then(async response => {
        if (!response.ok) throw new Error("Automated job messages could not be loaded.");
        const payload = await response.json() as { data?: { messages?: CommunicationMessage[] } };
        if (!controller.signal.aborted) setMessages(payload.data?.messages ?? []);
      })
      .catch(cause => { if (!controller.signal.aborted) setError(cause instanceof Error ? cause.message : "Automated job messages could not be loaded."); });
    return () => controller.abort();
  }, [jobId]);
  if (error) return <p role="alert" className="text-sm text-[var(--st-conflict)]">{error}</p>;
  if (messages === null || messages.length === 0) return null;
  return <section className="mb-4" aria-label="Automated job messages"><h3 className="mb-2 text-sm font-semibold text-[var(--ink)]">Automated messages</h3><Messages messages={messages} timezone={timezone} /></section>;
}

function JobMessageHistory({ jobId, threadIds, timezone }: { jobId: string; threadIds: string[]; timezone?: string }) {
  const [messages, setMessages] = useState<CommunicationMessage[] | null>(null);
  const [error, setError] = useState("");
  const [displayOffsetMinutes, setDisplayOffsetMinutes] = useState(0);
  const [retry, setRetry] = useState(0);
  useEffect(() => {
    const controller = new AbortController();
    const load = async () => {
      setMessages(null); setError("");
      const groups = await Promise.all(threadIds.map(async threadId => {
        const prefix = `historical-job-thread:${encodeURIComponent(jobId)}:`;
        let path: string;
        if (threadId === `historical-job:${jobId}`) {
          path = `/api/v1/workspace/communications/historical-job-messages/${encodeURIComponent(jobId)}`;
        } else if (threadId.startsWith(prefix) && threadId.slice(prefix.length)) {
          path = `/api/v1/workspace/communications/historical-job-thread-messages/${encodeURIComponent(jobId)}/${threadId.slice(prefix.length)}`;
        } else if (threadId.startsWith("historical-thread:") && threadId.slice("historical-thread:".length)) {
          // Hosted preview still uses customer-thread IDs on the job's verified list.
          path = `/api/v1/workspace/communications/historical-threads/${encodeURIComponent(threadId.slice("historical-thread:".length))}?${new URLSearchParams({ jobId })}`;
        } else {
          throw new Error("This conversation is not linked to this job.");
        }
        const response = await apiFetch(path, { signal: controller.signal });
        if (!response.ok) throw new Error("Job messages could not be loaded.");
        const payload = await response.json() as { data: { messages: Array<CommunicationMessage & { jobId?: string | null }>; groups?: unknown[] } };
        // Older APIs ignore jobId in the query and return the entire customer thread.
        // Keep only explicit job links even when the server has not been updated yet.
        return { messages: payload.data.messages.filter(message => message.jobId === jobId), projected: Boolean(payload.data.groups?.length) };
      }));
      const unique = new Map(groups.flatMap(group => group.messages).map(message => [message.id, message]));
      if (!controller.signal.aborted) {
        setMessages([...unique.values()].sort((a, b) => a.createdAt.localeCompare(b.createdAt) || a.id.localeCompare(b.id)));
        // Retain the verified presentation offset for the newer Job History projection.
        setDisplayOffsetMinutes(groups.some(group => group.projected) ? -600 : 0);
      }
    };
    void load().catch(cause => { if (!controller.signal.aborted) setError(cause instanceof Error ? cause.message : "Job messages could not be loaded."); });
    return () => controller.abort();
  }, [jobId, threadIds, retry]);
  return <div>
    {error ? <div><p role="alert" className="text-sm text-[var(--st-conflict)]">{error}</p><button type="button" className="mt-2 text-sm font-semibold underline" onClick={() => setRetry(value => value + 1)}>Try again</button></div>
      : messages === null ? <p role="status" className="text-sm text-[var(--ink-soft)]">Loading job messages…</p>
      : messages.length ? <Messages messages={messages} timezone={timezone} displayOffsetMinutes={displayOffsetMinutes} />
      : <p className="text-sm text-[var(--ink-soft)]">No saved messages are linked to this job.</p>}
  </div>;
}

/** Uses deployed conversation endpoints and verifies customer IDs before showing history. */
export function CustomerMessages({ customerId, endpoint, title = "Messages", description = "Customer conversation history · ServiceTitan archive", onOpenThread = () => {}, customerName: _customerName, timezone, inlineJobId }: { customerId?: string; endpoint?: string; title?: string; description?: string; onOpenThread?: (threadId: string) => void; customerName?: string; timezone?: string; inlineJobId?: string }) {
  const [page, setPage] = useState(0);
  const [threads, setThreads] = useState<HistoricalThread[]>([]);
  const threadIds = useMemo(() => threads.map(thread => thread.id), [threads]);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [retry, setRetry] = useState(0);
  useEffect(() => {
    const controller = new AbortController();
    setLoading(true); setError("");
    const params = new URLSearchParams({ page: String(page) });
    const path = endpoint ?? `/api/v1/workspace/customers/${encodeURIComponent(customerId!)}/historical-threads`;
    void apiFetch(`${path}?${params}`, { signal: controller.signal })
      .then(async (response) => {
        if (!response.ok) throw new Error("Customer messages could not be loaded.");
        const payload = await response.json() as { data: { items: HistoricalThread[]; hasMore?: boolean } };
        if (controller.signal.aborted) return;
        setThreads((current) => page === 0 ? payload.data.items : [...current, ...payload.data.items.filter((thread) => !current.some((item) => item.id === thread.id))]);
        setHasMore(Boolean(payload.data.hasMore));
      })
      .catch((cause) => { if (!controller.signal.aborted) setError(cause instanceof Error ? cause.message : "Customer messages could not be loaded."); })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [customerId, endpoint, page, retry]);
  if (inlineJobId) return <section aria-label="Job messages" className="py-3">
    {error && <div><p role="alert" className="text-sm text-[var(--st-conflict)]">{error}</p><button type="button" className="mt-2 text-sm font-semibold underline" onClick={() => setRetry(value => value + 1)}>Try again</button></div>}
    {loading && <p role="status" className="text-sm text-[var(--ink-soft)]">Loading job messages…</p>}
    {!loading && !error && !threads.length && <p className="text-sm text-[var(--ink-soft)]">No ServiceTitan message history is linked to this job.</p>}
    <JobAutomatedMessages jobId={inlineJobId} timezone={timezone} />
    {threads.length > 0 && <JobMessageHistory key={JSON.stringify(threadIds)} jobId={inlineJobId} threadIds={threadIds} timezone={timezone} />}
    {hasMore && <button type="button" disabled={loading} className="mt-4 text-sm font-semibold underline disabled:opacity-50" onClick={() => setPage(value => value + 1)}>Load more messages</button>}
  </section>;
  return <section aria-label="Customer messages" className="mt-6 rounded-xl border border-[var(--line)] bg-[var(--paper)] p-5">
    <h2 className="text-lg font-semibold text-[var(--ink)]">{title}</h2>
    <p className="mt-1 text-sm text-[var(--ink-soft)]">{description}</p>
    {error && <div className="mt-3"><p role="alert" className="text-sm text-[var(--st-conflict)]">{error}</p><button type="button" className="mt-2 text-sm font-semibold underline" onClick={() => setRetry((value) => value + 1)}>Try again</button></div>}
    {loading && <p role="status" className="mt-3 text-sm text-[var(--ink-soft)]">Loading customer messages…</p>}
    {!loading && !error && !threads.length && <p className="mt-3 text-sm text-[var(--ink-soft)]">No saved conversations are linked to this customer.</p>}
    <div className="mt-4 space-y-3">{threads.map((thread) => <article key={thread.id} className="rounded-lg border border-[var(--line)] p-3">
      <button type="button" className="w-full text-left text-sm" onClick={() => onOpenThread(thread.id)}>
        <span className="block font-semibold">{thread.messageCount ?? ""} messages{thread.phone ? ` · ${thread.phone}` : ""}</span>
        {thread.summary && <span className="mt-1 block line-clamp-2 text-[var(--ink-soft)]">{thread.summary}</span>}
        <span className="mt-2 block font-semibold text-[var(--ink)]">Open in Texts</span>
      </button>
    </article>)}</div>
    {hasMore && <button type="button" disabled={loading} className="mt-4 text-sm font-semibold underline disabled:opacity-50" onClick={() => setPage((value) => value + 1)}>Load more conversations</button>}
  </section>;
}
